import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  getDoc,
  serverTimestamp,
  query,
  orderBy,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useSyncStore } from './sync'
import { writeAuditLog } from '@/services/audit'
import type { Subject, FlyerData } from '@/types'

function subjectsCol(incidentId: string) {
  return collection(db, 'incidents', incidentId, 'subjects')
}

function subjectDoc(incidentId: string, subjectId: string) {
  return doc(db, 'incidents', incidentId, 'subjects', subjectId)
}

export const useSubjectsStore = defineStore('subjects', () => {
  const subjects = ref<Subject[]>([])
  const activeSubjectId = ref<string | null>(null)
  const loading = ref(false)

  let unsubscribe: Unsubscribe | null = null
  let _incidentId = ''

  const activeSubject = computed<Subject | null>(() => {
    if (subjects.value.length === 0) return null
    if (activeSubjectId.value) {
      return subjects.value.find((s) => s.subject_id === activeSubjectId.value) ?? subjects.value[0] ?? null
    }
    return subjects.value[0] ?? null
  })

  function subscribeToSubjects(incidentId: string) {
    if (incidentId === _incidentId && unsubscribe) return
    if (unsubscribe) unsubscribe()
    _incidentId = incidentId
    loading.value = true
    const q = query(subjectsCol(incidentId), orderBy('created_at', 'asc'))
    unsubscribe = onSnapshot(q, (snap) => {
      subjects.value = snap.docs.map((d) => {
        const data = d.data()
        return {
          ...data,
          subject_id: d.id,
          photo_url: data.photo_url ?? null,
          lkp_lat: data.lkp_lat != null ? Number(data.lkp_lat) : null,
          lkp_lng: data.lkp_lng != null ? Number(data.lkp_lng) : null,
        } as Subject
      })
      loading.value = false
    })
  }

  async function createSubject(
    incidentId: string,
    data: Partial<Subject> & { first_name: string; last_name: string },
  ): Promise<string> {
    const syncStore = useSyncStore()
    const payload = {
      ...data,
      incident_id: incidentId,
      created_at: serverTimestamp(),
    }
    syncStore.pendingWrites++
    try {
      const ref = await addDoc(subjectsCol(incidentId), payload)
      await writeAuditLog(incidentId, 'subject.created', 'subject', ref.id, null, payload)
      return ref.id
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function updateSubjectField(
    incidentId: string,
    subjectId: string,
    field: string,
    newValue: unknown,
  ): Promise<void> {
    const syncStore = useSyncStore()
    // Read current value for audit log before state
    const snap = await getDoc(subjectDoc(incidentId, subjectId))
    const before = snap.exists() ? { [field]: snap.data()[field] ?? null } : { [field]: null }
    const after = { [field]: newValue }

    syncStore.pendingWrites++
    try {
      await updateDoc(subjectDoc(incidentId, subjectId), after)
      await writeAuditLog(incidentId, 'subject.updated', 'subject', subjectId, before, after)
    } finally {
      syncStore.pendingWrites--
    }
  }

  async function saveFlyerDraft(
    incidentId: string,
    subjectId: string,
    draft: FlyerData,
  ): Promise<void> {
    await updateDoc(subjectDoc(incidentId, subjectId), { flyer_draft: draft })
  }

  async function savePermanentFlyerUrl(
    incidentId: string,
    subjectId: string,
    url: string,
  ): Promise<void> {
    await updateDoc(subjectDoc(incidentId, subjectId), { permanent_flyer_url: url })
  }

  function setActiveSubject(subjectId: string) {
    activeSubjectId.value = subjectId
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    _incidentId = ''
    subjects.value = []
    activeSubjectId.value = null
    loading.value = false
  }

  return {
    subjects,
    activeSubjectId,
    loading,
    activeSubject,
    subscribeToSubjects,
    createSubject,
    updateSubjectField,
    saveFlyerDraft,
    savePermanentFlyerUrl,
    setActiveSubject,
    teardown,
  }
})
